import { Switch, Route, useLocation, Router as WouterRouter } from "wouter";
import { useEffect, useRef, lazy, Suspense } from "react";
import { queryClient } from "./lib/queryClient";
import { drainClickQueue } from "@/lib/trackSearch";
import { QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { ClerkProvider, SignIn, SignUp, useClerk } from "@clerk/react";
import { publishableKeyFromHost } from "@clerk/react/internal";
import { neobrutalism } from "@clerk/themes";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/ThemeProvider";
import { HelmetProvider } from "react-helmet-async";
import { useBootstrapData } from "@/lib/bootstrap";
import { trackPageView } from "@/lib/analytics";
import Home from "@/pages/Home";
import NotFound from "@/pages/not-found";

const OnboardingToast = lazy(() => import("@/components/OnboardingToast").then(m => ({ default: m.OnboardingToast })));
const EarlyAccessToast = lazy(() => import("@/components/EarlyAccessToast").then(m => ({ default: m.EarlyAccessToast })));

const clerkPubKey = publishableKeyFromHost(
  window.location.hostname,
  import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
);

const clerkProxyUrl = import.meta.env.VITE_CLERK_PROXY_URL;
const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

function stripBase(path: string): string {
  return basePath && path.startsWith(basePath)
    ? path.slice(basePath.length) || "/"
    : path;
}

if (!clerkPubKey) {
  throw new Error("Missing VITE_CLERK_PUBLISHABLE_KEY in .env file");
}

const clerkAppearance = {
  theme: neobrutalism,
  options: {
    logoPlacement: "inside" as const,
    logoLinkUrl: basePath || "/",
    logoImageUrl: `${window.location.origin}${basePath}/logo.svg`,
  },
  variables: {
    colorPrimary: "#111111",
    colorForeground: "#111111",
    colorMutedForeground: "#5d5549",
    colorDanger: "#dc2626",
    colorBackground: "#fffdf9",
    colorInput: "#ffffff",
    colorInputForeground: "#111111",
    colorNeutral: "#111111",
    fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
    borderRadius: "2px",
  },
  elements: {
    rootBox: "w-full flex justify-center",
    cardBox: "w-[440px] max-w-full overflow-hidden border-2 border-black bg-[#fffdf9] shadow-[4px_4px_0_#000]",
    card: "!shadow-none !border-0 !bg-transparent !rounded-none",
    footer: "!shadow-none !border-0 !bg-transparent !rounded-none",
    headerTitle: "font-black uppercase tracking-wide text-black",
    headerSubtitle: "text-[#5d5549]",
    socialButtonsBlockButtonText: "text-black",
    formFieldLabel: "font-bold text-black",
    footerActionLink: "font-bold text-black underline",
    footerActionText: "text-[#5d5549]",
    dividerText: "text-[#5d5549]",
    identityPreviewEditButton: "text-black",
    formFieldSuccessText: "text-green-700",
    alertText: "text-black",
    logoBox: "mb-4",
    logoImage: "h-14 w-14",
    socialButtonsBlockButton: "border-2 border-black bg-white shadow-[2px_2px_0_#000]",
    formButtonPrimary: "border-2 border-black bg-[#a7f3d0] text-black shadow-[3px_3px_0_#000]",
    formFieldInput: "border-2 border-black bg-white text-black",
    footerAction: "border-t-2 border-black",
    dividerLine: "bg-black",
    alert: "border-2 border-black bg-[#fff4e8]",
    otpCodeFieldInput: "border-2 border-black bg-white text-black",
    formFieldRow: "gap-2",
    main: "gap-4",
  },
};

// Lazy load non-critical pages for faster initial load
const AdminPanel = lazy(() => import("@/pages/AdminPanel"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const Disclaimer = lazy(() => import("@/pages/Disclaimer"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const MyLarks = lazy(() => import("@/pages/MyLarks"));
const EventDetail = lazy(() => import("@/pages/EventDetail"));
const SeriesPage = lazy(() => import("@/pages/SeriesPage"));
const VenuePage = lazy(() => import("@/pages/VenuePage"));

// Loading fallback component
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-pulse text-muted-foreground">Loading...</div>
    </div>
  );
}

function BootstrapLoader() {
  const { data } = useBootstrapData();

  useEffect(() => {
    if (!data) return;
    
    // Seed individual query caches from bootstrap data (exact formats that components expect)
    queryClient.setQueryData(['/api/settings/logo'], { logoUrl: data.branding.logoUrl });
    queryClient.setQueryData(['/api/settings/logo-size'], { logoSize: data.branding.logoSize });
    queryClient.setQueryData(['/api/settings/page-title'], { title: data.branding.pageTitle });
    queryClient.setQueryData(['/api/settings/page-background'], data.layout.pageBackground);
    queryClient.setQueryData(['/api/settings/font-sizes'], data.layout.fontSizes);
    queryClient.setQueryData(['/api/settings/header-tagline'], { tagline: data.header.tagline });
    queryClient.setQueryData(['/api/settings/header-tagline-style'], data.header.taglineStyle);
    queryClient.setQueryData(['/api/settings/header-tagline-visible'], { visible: data.header.taglineVisible });
    queryClient.setQueryData(['/api/settings/search-placeholder'], { placeholder: data.header.searchPlaceholder });
    // Ensure icons is always an array for header-icons
    queryClient.setQueryData(['/api/settings/header-icons'], Array.isArray(data.header.icons) ? data.header.icons : []);
    queryClient.setQueryData(['/api/settings/community-messaging'], data.messaging.communityMessaging);
    queryClient.setQueryData(['/api/settings/onboarding-toast'], data.messaging.onboardingToast);
    queryClient.setQueryData(['/api/settings/early-access-toast-enabled'], { enabled: data.messaging.earlyAccessToastEnabled });
    queryClient.setQueryData(['/api/settings/auto-scroll-speed'], { speed: data.interaction.autoScrollSpeed });
    queryClient.setQueryData(['/api/settings/user-accounts'], data.features.userAccounts);
    queryClient.setQueryData(['/api/settings/event-display'], data.features.eventDisplay);

    // Apply font sizes immediately so text doesn't jump after initial load
    const fontSizes = data.layout.fontSizes;
    if (fontSizes) {
      const root = document.documentElement;
      const isMobile = window.innerWidth < 768;
      
      if (isMobile) {
        root.style.setProperty('--font-scale-small', fontSizes.mobileSmall?.toString() || '0.875');
        root.style.setProperty('--font-scale-medium', fontSizes.mobileMedium?.toString() || '1');
        root.style.setProperty('--font-scale-large', fontSizes.mobileLarge?.toString() || '1.125');
      } else {
        root.style.setProperty('--font-scale-small', fontSizes.small?.toString() || '0.875');
        root.style.setProperty('--font-scale-medium', fontSizes.medium?.toString() || '1');
        root.style.setProperty('--font-scale-large', fontSizes.large?.toString() || '1.125');
      }
      
      // Apply the data-font-size attribute if not already set (default to medium)
      const storedSize = localStorage.getItem("fontSize");
      if (storedSize && ["small", "medium", "large"].includes(storedSize)) {
        root.setAttribute("data-font-size", storedSize);
      } else {
        root.setAttribute("data-font-size", data.layout.fontSizes.defaultSize || "medium");
      }
    }
  }, [data]);

  return null;
}

function Router() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/regions/:regionSlug/today">{() => <Home routeDateIntent="today" />}</Route>
        <Route path="/regions/:regionSlug/tomorrow">{() => <Home routeDateIntent="tomorrow" />}</Route>
        <Route path="/regions/:regionSlug/this-weekend">{() => <Home routeDateIntent="weekend" />}</Route>
        <Route path="/regions/:regionSlug" component={Home} />
        <Route path="/categories/:categorySlug" component={Home} />
        <Route path="/today">{() => <Home routeDateIntent="today" />}</Route>
        <Route path="/tomorrow">{() => <Home routeDateIntent="tomorrow" />}</Route>
        <Route path="/this-weekend">{() => <Home routeDateIntent="weekend" />}</Route>
        <Route path="/events/:slug" component={EventDetail} />
        <Route path="/series/:slug" component={SeriesPage} />
        <Route path="/venues/:slug" component={VenuePage} />
        <Route path="/admin" component={AdminPanel} />
        <Route path="/about" component={About} />
        <Route path="/contact" component={Contact} />
        <Route path="/disclaimer" component={Disclaimer} />
        <Route path="/privacy" component={Privacy} />
        <Route path="/my-larks" component={MyLarks} />
        <Route path="/sign-in/*?" component={SignInPage} />
        <Route path="/sign-up/*?" component={SignUpPage} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function SignInPage() {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-background px-4">
      <SignIn routing="path" path={`${basePath}/sign-in`} signUpUrl={`${basePath}/sign-up`} />
    </div>
  );
}

function SignUpPage() {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-background px-4">
      <SignUp routing="path" path={`${basePath}/sign-up`} signInUrl={`${basePath}/sign-in`} />
    </div>
  );
}

function ClerkQueryClientCacheInvalidator() {
  const { addListener } = useClerk();
  const queryClient = useQueryClient();
  const prevUserIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const unsubscribe = addListener(({ user }) => {
      const userId = user?.id ?? null;
      if (prevUserIdRef.current !== undefined && prevUserIdRef.current !== userId) {
        queryClient.clear();
      }
      prevUserIdRef.current = userId;
    });
    return unsubscribe;
  }, [addListener, queryClient]);

  return null;
}

function AppShell() {
  const [location] = useLocation();
  const isEventDetailRoute = location.startsWith("/events/");

  // One GA4 page view per page. Filter and search changes only rewrite the query
  // string, so they don't count. The short wait lets the new page set its title.
  useEffect(() => {
    const timer = window.setTimeout(trackPageView, 300);
    return () => window.clearTimeout(timer);
  }, [location]);

  // Home renders the onboarding message inline in its event list so it never covers event cards.
  const isHomeListRoute =
    location === "/" ||
    /^\/(today|tomorrow|this-weekend)\/?$/.test(location) ||
    location.startsWith("/regions/") ||
    location.startsWith("/categories/");

  return (
    <>
      {!isEventDetailRoute && <BootstrapLoader />}
      <Toaster />
      {!isEventDetailRoute && (
        <Suspense fallback={null}>
          {!isHomeListRoute && <OnboardingToast />}
          <EarlyAccessToast />
        </Suspense>
      )}
      <Router />
    </>
  );
}

function ClerkProviderWithRoutes() {
  const [, setLocation] = useLocation();

  useEffect(() => {
    setTimeout(drainClickQueue, 1000);
  }, []);

  return (
    <ClerkProvider
      publishableKey={clerkPubKey}
      proxyUrl={clerkProxyUrl}
      appearance={clerkAppearance}
      signInUrl={`${basePath}/sign-in`}
      signUpUrl={`${basePath}/sign-up`}
      localization={{
        signIn: {
          start: {
            title: "Welcome back",
            subtitle: "Sign in to save your favorite events",
          },
        },
        signUp: {
          start: {
            title: "Create your account",
            subtitle: "Save events and keep up with what is happening",
          },
        },
      }}
      routerPush={(to) => setLocation(stripBase(to))}
      routerReplace={(to) => setLocation(stripBase(to), { replace: true })}
    >
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <ClerkQueryClientCacheInvalidator />
          <ThemeProvider>
            <TooltipProvider>
              <AppShell />
            </TooltipProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </ClerkProvider>
  );
}

function App() {
  return (
    <WouterRouter base={basePath}>
      <ClerkProviderWithRoutes />
    </WouterRouter>
  );
}

export default App;
